"use client";

import { usePathname } from "@/i18n/navigation";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ShepherdJourneyContext } from "react-shepherd";

type walkthrough_debug_snapshot = {
	pathname: string;
	active_tour_id: string | null;
	current_step_id: string | null;
	step_index: number | null;
	step_total: number;
	keyboard_visible: boolean;
	keyboard_present: boolean;
	overlay_count: number;
	overlay_pointer_events: string | null;
	overlay_opacity: string | null;
	active_element: string | null;
	walkthrough_flags: string[];
};

const format_time = () => {
	const now = new Date();
	return now.toLocaleTimeString("en-GB", { hour12: false });
};

const keyboard_selectors = [
	"math-virtual-keyboard",
	".ML__keyboard",
	'[class*="MLK__"]',
	'[class*="virtual-keyboard"]'
].join(", ");
const WALKTHROUGH_DEBUG_ENABLED = process.env.NODE_ENV === "development";

const build_active_element_label = (element: Element | null) => {
	if (!element) return null;
	const tag = element.tagName?.toLowerCase() ?? "unknown";
	const id = (element as HTMLElement).id ? `#${(element as HTMLElement).id}` : "";
	const class_name = ((element as HTMLElement).className ?? "").toString().trim();
	const short_class = class_name ? `.${class_name.split(/\s+/).slice(0, 2).join(".")}` : "";
	return `${tag}${id}${short_class}`;
};

const get_keyboard_visible = (): { visible: boolean; present: boolean } => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return { visible: false, present: false };
	}

	const api_visible = Boolean((window as any)?.mathVirtualKeyboard?.visible);
	const keyboard_nodes = Array.from(document.querySelectorAll(keyboard_selectors)) as HTMLElement[];
	const has_active_mathfield = document.activeElement?.tagName?.toLowerCase() === "math-field";

	const dom_visible = keyboard_nodes.some((keyboard_node) => {
		const style = getComputedStyle(keyboard_node);
		if (style.display === "none" || style.visibility === "hidden") return false;
		const rect = keyboard_node.getBoundingClientRect();
		return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
	});

	const present = api_visible || keyboard_nodes.length > 0 || has_active_mathfield;
	const visible = api_visible || dom_visible || has_active_mathfield;
	return { visible, present };
};

const get_walkthrough_flags = () => {
	if (typeof window === "undefined") return [];
	return Object.keys(localStorage)
		.filter((key) => key.startsWith("walkthrough__"))
		.filter((key) => localStorage.getItem(key) === "true")
		.sort();
};

const get_walkthrough_flag_keys = () => {
	if (typeof window === "undefined") return [];
	return Object.keys(localStorage)
		.filter((key) => key.startsWith("walkthrough__"))
		.sort();
};

export default function WalkthroughDebugPanel() {
	const pathname = usePathname();
	const journey = useContext(ShepherdJourneyContext);
	const [is_open, set__is_open] = useState(false);
	const [snapshot, set__snapshot] = useState<walkthrough_debug_snapshot | null>(null);
	const [events, set__events] = useState<string[]>([]);
	const previous_signature_ref = useRef<string>("");

	const get_snapshot = () => {
		const active_tour = journey?.Shepherd?.activeTour;
		const steps = active_tour?.steps ?? [];
		const current_step: any = active_tour?.currentStep ?? null;
		const current_step_id = current_step?.id ?? null;
		const step_index = current_step
			? Math.max(
				steps.findIndex((step: any) => step === current_step || step?.id === current_step_id),
				0
			)
			: null;

		const overlays = Array.from(document.querySelectorAll(".shepherd-modal-overlay-container")) as HTMLElement[];
		const first_overlay = overlays[0] ?? null;
		const keyboard_state = get_keyboard_visible();

		return {
			pathname,
			active_tour_id: active_tour?.id ?? null,
			current_step_id,
			step_index,
			step_total: steps.length,
			keyboard_visible: keyboard_state.visible,
			keyboard_present: keyboard_state.present,
			overlay_count: overlays.length,
			overlay_pointer_events: first_overlay ? getComputedStyle(first_overlay).pointerEvents : null,
			overlay_opacity: first_overlay ? getComputedStyle(first_overlay).opacity : null,
			active_element:
				typeof document !== "undefined" ? build_active_element_label(document.activeElement) : null,
			walkthrough_flags: get_walkthrough_flags()
		} satisfies walkthrough_debug_snapshot;
	};

	useEffect(() => {
		if (!WALKTHROUGH_DEBUG_ENABLED) return;

		const on_keydown = (event: KeyboardEvent) => {
			const is_toggle = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "d";
			if (!is_toggle) return;
			event.preventDefault();
			set__is_open((current) => !current);
		};

		window.addEventListener("keydown", on_keydown);
		return () => window.removeEventListener("keydown", on_keydown);
	}, []);

	useEffect(() => {
		if (!WALKTHROUGH_DEBUG_ENABLED || !is_open) return;

		const tick = () => {
			const next_snapshot = get_snapshot();
			set__snapshot(next_snapshot);

			const signature = [
				next_snapshot.active_tour_id ?? "none",
				next_snapshot.current_step_id ?? "none",
				String(next_snapshot.keyboard_visible),
				String(next_snapshot.overlay_count),
				next_snapshot.active_element ?? "none"
			].join("|");

			if (signature !== previous_signature_ref.current) {
				previous_signature_ref.current = signature;
				set__events((current) => {
					const next_entry = `[${format_time()}] tour=${next_snapshot.active_tour_id ?? "none"} step=${next_snapshot.current_step_id ?? "none"} keyboard=${next_snapshot.keyboard_visible ? "open" : "closed"} overlay=${next_snapshot.overlay_count}`;
					return [next_entry, ...current].slice(0, 14);
				});
			}
		};

		tick();
		const interval = window.setInterval(tick, 180);
		return () => window.clearInterval(interval);
	}, [is_open, pathname, journey]);

	const step_progress = useMemo(() => {
		if (!snapshot || snapshot.step_index === null || snapshot.step_total <= 0) return "n/a";
		return `${snapshot.step_index + 1}/${snapshot.step_total}`;
	}, [snapshot]);

	const clear_walkthrough_flags = (scope: "all" | "current") => {
		if (typeof window === "undefined") return;

		if (scope === "current") {
			const tour_id = snapshot?.active_tour_id;
			if (!tour_id) return;
			localStorage.removeItem(`walkthrough__${tour_id}`);
			set__events((current) => [`[${format_time()}] cleared walkthrough__${tour_id}`, ...current].slice(0, 14));
		} else {
			const keys = get_walkthrough_flag_keys();
			keys.forEach((key) => localStorage.removeItem(key));
			set__events((current) => [`[${format_time()}] cleared all walkthrough flags (${keys.length})`, ...current].slice(0, 14));
		}

		set__snapshot(get_snapshot());
	};

	if (!WALKTHROUGH_DEBUG_ENABLED || !is_open) return null;

	return (
		<div className="fixed right-4 top-4 z-100000 w-[360px] max-w-[92vw] rounded-xl border border-slate-700 bg-slate-950/95 p-3 text-xs text-slate-100 shadow-2xl backdrop-blur">
			<div className="mb-2 flex items-center justify-between">
				<p className="font-semibold tracking-wide">Walkthrough Debug</p>
				<div className="flex items-center gap-1.5">
					<button
						type="button"
						className="rounded border border-amber-500/60 px-2 py-0.5 text-[11px] text-amber-200 hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-40"
						onClick={() => clear_walkthrough_flags("current")}
						disabled={!snapshot?.active_tour_id}
						title="Clear completion key for the currently active tour"
					>
						Clear current
					</button>
					<button
						type="button"
						className="rounded border border-rose-500/60 px-2 py-0.5 text-[11px] text-rose-200 hover:bg-rose-500/20"
						onClick={() => clear_walkthrough_flags("all")}
						title="Clear all walkthrough completion keys"
					>
						Clear all
					</button>
					<button
						type="button"
						className="rounded border border-slate-500 px-2 py-0.5 text-[11px] hover:bg-slate-800"
						onClick={() => set__is_open(false)}
					>
						Close
					</button>
				</div>
			</div>

			{snapshot ? (
				<div className="space-y-1">
					<p><span className="text-slate-400">Path:</span> {snapshot.pathname}</p>
					<p><span className="text-slate-400">Tour:</span> {snapshot.active_tour_id ?? "none"}</p>
					<p><span className="text-slate-400">Step:</span> {snapshot.current_step_id ?? "none"} ({step_progress})</p>
					<p><span className="text-slate-400">Keyboard:</span> {snapshot.keyboard_visible ? "open" : "closed"} / present={String(snapshot.keyboard_present)}</p>
					<p><span className="text-slate-400">Overlay:</span> count={snapshot.overlay_count} pointer={snapshot.overlay_pointer_events ?? "n/a"} opacity={snapshot.overlay_opacity ?? "n/a"}</p>
					<p><span className="text-slate-400">Active element:</span> {snapshot.active_element ?? "none"}</p>
					<p><span className="text-slate-400">Flags:</span> {snapshot.walkthrough_flags.length ? snapshot.walkthrough_flags.join(", ") : "none"}</p>
				</div>
			) : (
				<p className="text-slate-300">Loading snapshot…</p>
			)}

			<div className="mt-3 rounded-md border border-slate-700 bg-slate-900/70 p-2">
				<p className="mb-1 text-[11px] font-semibold text-slate-300">Recent events</p>
				<div className="max-h-36 overflow-auto space-y-1">
					{events.length === 0 ? (
						<p className="text-[11px] text-slate-400">No events yet.</p>
					) : (
						events.map((entry, index) => (
							<p key={`${entry}-${index}`} className="font-mono text-[11px] text-slate-200">
								{entry}
							</p>
						))
					)}
				</div>
			</div>

			<p className="mt-2 text-[11px] text-slate-400">Toggle: Cmd/Ctrl + Shift + D</p>
		</div>
	);
}
